import { parseDate, type CalendarDate } from '@internationalized/date';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button as AriaButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker as AriaDatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from 'react-aria-components';
import { useI18n } from '../i18n/I18nProvider';
import { cx } from './cx';
import styles from './Field.module.css';
import { FieldHelp, FieldLabel, type FieldChromeProps } from './TextField';

export interface DateFieldProps extends FieldChromeProps {
  /** 'YYYY-MM-DD' or null. Calendar dates are never shifted by time zones. */
  value: string | null;
  onChange: (value: string | null) => void;
  minValue?: string;
  maxValue?: string;
  isDisabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

function toCalendarDate(value: string | null | undefined): CalendarDate | null {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch (error) {
    if (error instanceof RangeError) return null;
    throw error;
  }
}

/** Date picker (typed segments + calendar), localized by React Aria (RTL, digits, week start). */
export function DateField({
  label,
  description,
  errorMessage,
  isRequired,
  isOptional,
  fieldName,
  value,
  onChange,
  minValue,
  maxValue,
  isDisabled,
  className,
  ...props
}: DateFieldProps) {
  const { t } = useI18n();
  const min = toCalendarDate(minValue);
  const max = toCalendarDate(maxValue);
  return (
    <AriaDatePicker
      {...props}
      value={toCalendarDate(value)}
      onChange={(next) => onChange(next ? next.toString() : null)}
      {...(min ? { minValue: min } : {})}
      {...(max ? { maxValue: max } : {})}
      isDisabled={isDisabled ?? false}
      isInvalid={Boolean(errorMessage)}
      isRequired={isRequired ?? false}
      validationBehavior="aria"
      granularity="day"
      className={cx(styles.field, className)}
      data-field={fieldName}
    >
      <FieldLabel label={label} isRequired={isRequired ?? false} isOptional={isOptional ?? false} />
      <Group className={styles.control}>
        <DateInput className={styles.dateInput}>
          {(segment) => <DateSegment segment={segment} className={styles.segmentPart} />}
        </DateInput>
        <AriaButton className={styles.trigger} aria-label={t.common.openCalendar}>
          <CalendarDays size={16} aria-hidden="true" />
        </AriaButton>
      </Group>
      <FieldHelp description={description} errorMessage={errorMessage} />
      <Popover className={styles.popover} offset={4} placement="bottom end">
        <Dialog
          className={styles.calendar}
          aria-label={typeof label === 'string' ? label : undefined}
        >
          <Calendar>
            <header className={styles.calendarHeader}>
              <AriaButton
                slot="previous"
                className={styles.trigger}
                aria-label={t.common.previousMonth}
              >
                <ChevronLeft size={16} className="flip-rtl" aria-hidden="true" />
              </AriaButton>
              <Heading className={styles.calendarHeading} />
              <AriaButton slot="next" className={styles.trigger} aria-label={t.common.nextMonth}>
                <ChevronRight size={16} className="flip-rtl" aria-hidden="true" />
              </AriaButton>
            </header>
            <CalendarGrid className={styles.calendarGrid}>
              {(date) => <CalendarCell date={date} className={styles.calendarCell} />}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </AriaDatePicker>
  );
}
